import React from 'react'
import { Card, CardContent, Skeleton } from '@t-one-internal/design-system'
import { useRecognitionStore } from '../../../stores/recognitionStore'

// Rank colors for top 5: gold, silver, bronze, then muted tones
// const RANK_COLORS = ['#D97706', '#6B7280', '#B45309', '#64748B', '#94A3B8']

import { hashValueColor } from '../utils/valueColors'

// function makeInitials(name: string): string {
//   return name
//     .split(' ')
//     .map((w) => w[0])
//     .join('')
//     .toUpperCase()
//     .slice(0, 2) || '?'
// }

const FeedSidebar: React.FC = () => {
  const myStats = useRecognitionStore((s) => s.myStats)
  const statsLoading = useRecognitionStore((s) => s.statsLoading)
  const trendingValues = useRecognitionStore((s) => s.trendingValues)
  // const topRecognized = useRecognitionStore((s) => s.topRecognized)
  const dashboardLoading = useRecognitionStore((s) => s.dashboardLoading)

  return (
    <div className="w-[300px] border-l border-border p-5 shrink-0 space-y-5 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
      {/* My Recognition */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            My Recognition
          </h3>
          {statsLoading ? (
            <div className="grid grid-cols-2 gap-2.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="text-center p-2.5 bg-muted rounded-lg">
                  <Skeleton className="h-6 w-10 mx-auto mb-1" />
                  <Skeleton className="h-3 w-14 mx-auto" />
                </div>
              ))}
            </div>
          ) : myStats ? (
            <div className="grid grid-cols-2 gap-2.5">
              <div className="text-center p-2.5 bg-muted rounded-lg">
                <div className="text-xl font-bold text-primary">{myStats.received}</div>
                <div className="text-[11px] text-muted-foreground">Received</div>
              </div>
              <div className="text-center p-2.5 bg-muted rounded-lg">
                <div className="text-xl font-bold text-green-600">{myStats.given}</div>
                <div className="text-[11px] text-muted-foreground">Given</div>
              </div>
              <div className="text-center p-2.5 bg-muted rounded-lg">
                <div className="text-xl font-bold text-amber-500">{myStats.points.toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground">Points</div>
              </div>
              <div className="text-center p-2.5 bg-muted rounded-lg">
                <div className="text-xl font-bold text-violet-500">{myStats.xp.toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground">XP</div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Trending Values */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Trending Values
          </h3>
          {dashboardLoading ? (
            <div className="space-y-2.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                </div>
              ))}
            </div>
          ) : trendingValues.length > 0 ? (
            <div className="space-y-2.5">
              {trendingValues.map((value, i) => {
                const vc = hashValueColor(value.name)
                return (
                  <div key={value.value_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-muted-foreground w-4">#{i + 1}</span>
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ color: vc.text, backgroundColor: vc.bg }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: vc.text }} />
                        {value.name}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No trending data yet</p>
          )}
        </CardContent>
      </Card>

      {/* Top Recognized — hidden for now
      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Top Recognized This Month
          </h3>
          {dashboardLoading ? (
            <div className="space-y-2.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <div>
                    <Skeleton className="h-3 w-20 mb-1" />
                    <Skeleton className="h-3 w-14" />
                  </div>
                </div>
              ))}
            </div>
          ) : topRecognized.length > 0 ? (
            <div className="space-y-2.5">
              {topRecognized.map((entry, i) => (
                <div key={entry.employee_id} className="flex items-center gap-2.5">
                  <span
                    className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                    style={{
                      color: '#fff',
                      backgroundColor: RANK_COLORS[i] || '#94A3B8',
                    }}
                  >
                    {i + 1}
                  </span>
                  <Avatar className="w-7 h-7">
                    {entry.profile_pic_url ? (
                      <AvatarImage src={entry.profile_pic_url} alt={entry.employee_name} />
                    ) : null}
                    <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                      {makeInitials(entry.employee_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{entry.employee_name}</p>
                    <p className="text-[11px] text-muted-foreground">{entry.recognition_count} recognitions</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No recognitions this month yet</p>
          )}
        </CardContent>
      </Card>
      */}
    </div>
  )
}

export default FeedSidebar

